import type { ReactNode } from "react";
import { Bg, Coin, Cube, L0, L1, L2, Pill, Rays, Sparkle, Star5, type SceneProps } from "./prims";

/* v16 – illustrations for Ludo, Yatzy, Sudoku and Videnspar (same 640×360 / three-layer recipe as the others) */

const PIP: Record<number, [number, number][]> = {
  1: [[0, 0]], 2: [[-1, -1], [1, 1]], 3: [[-1, -1], [0, 0], [1, 1]], 4: [[-1, -1], [1, -1], [-1, 1], [1, 1]],
  5: [[-1, -1], [1, -1], [0, 0], [-1, 1], [1, 1]], 6: [[-1, -1], [1, -1], [-1, 0], [1, 0], [-1, 1], [1, 1]],
};
/** a flat die with a soft edge and pips, centred on (x, y) */
function Die({ x, y, s = 56, n, rot = 0, body = "#ffffff", pip = "#14246a" }: { x: number; y: number; s?: number; n: number; rot?: number; body?: string; pip?: string }) {
  const g = s * 0.26;
  return (
    <g transform={`translate(${x} ${y}) rotate(${rot})`}>
      <rect x={-s / 2 + 3} y={-s / 2 + 8} width={s} height={s} rx={s * 0.2} fill="#000" opacity={0.22} />
      <rect x={-s / 2} y={-s / 2 + 4} width={s} height={s} rx={s * 0.2} fill="#c3cff0" />
      <rect x={-s / 2} y={-s / 2} width={s} height={s} rx={s * 0.2} fill={body} stroke="#e1e8fb" strokeWidth={2} />
      {PIP[n]!.map(([a, b], i) => <circle key={i} cx={a * g} cy={b * g} r={s * 0.085} fill={pip} />)}
    </g>
  );
}

/* ─────────────── Ludo: the cross-shaped board, four colours, pawns and a die ─────────────── */
function Pawn({ x, y, s = 1, c, d }: { x: number; y: number; s?: number; c: string; d: string }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${s})`}>
      <ellipse cx={2} cy={2} rx={15} ry={6} fill="#000" opacity={0.28} />
      <path d="M-14 0 Q-14 -8 -6 -11 Q-9 -16 -9 -21 A9 9 0 1 1 9 -21 Q9 -16 6 -11 Q14 -8 14 0Z" fill={c} stroke={d} strokeWidth={2} strokeLinejoin="round" />
      <ellipse cx={-3} cy={-25} rx={3} ry={4.5} fill="#fff" opacity={0.65} />
    </g>
  );
}
export function Ludo({ u }: SceneProps) {
  const C = 19, o = 320 - C * 7.5, t = 180 - C * 7.5; // 15×15 cells
  const P = { r: ["#ff5a5f", "#c92a35"], b: ["#3d8bff", "#1d4fb8"], y: ["#ffc61a", "#d98200"], g: ["#2ecb73", "#16813f"] } as const;
  const cell = (i: number, j: number, fill: string, k?: string) => <rect key={k ?? `${i}-${j}`} x={o + i * C} y={t + j * C} width={C} height={C} fill={fill} stroke="#14246a" strokeOpacity={0.35} strokeWidth={1} />;
  const cells: ReactNode[] = [];
  for (let i = 0; i < 15; i++) for (let j = 0; j < 15; j++) {
    const arm = (i >= 6 && i <= 8) || (j >= 6 && j <= 8);
    if (!arm) continue;
    let f = "#fffaf0";
    if (j === 7 && i >= 1 && i <= 5) f = P.r[0]; if (i === 7 && j >= 1 && j <= 5) f = P.b[0];
    if (j === 7 && i >= 9 && i <= 13) f = P.y[0]; if (i === 7 && j >= 9 && j <= 13) f = P.g[0];
    if (i === 1 && j === 6) f = P.r[0]; if (i === 8 && j === 1) f = P.b[0]; if (i === 13 && j === 8) f = P.y[0]; if (i === 6 && j === 13) f = P.g[0];
    cells.push(cell(i, j, f));
  }
  const base = (x0: number, y0: number, c: readonly [string, string]) => (
    <g key={`${x0}${y0}`}>
      <rect x={o + x0 * C} y={t + y0 * C} width={6 * C} height={6 * C} fill={c[0]} stroke="#14246a" strokeOpacity={0.4} strokeWidth={1.5} />
      <rect x={o + (x0 + 1) * C} y={t + (y0 + 1) * C} width={4 * C} height={4 * C} rx={C * 0.6} fill="#fffaf0" />
      {[[1.5, 1.5], [3.5, 1.5], [1.5, 3.5], [3.5, 3.5]].map(([a, b], i) => <circle key={i} cx={o + (x0 + a! + 0.5) * C} cy={t + (y0 + b! + 0.5) * C} r={C * 0.42} fill={c[0]} opacity={0.35} />)}
    </g>
  );
  const centre = [o + 7.5 * C, t + 7.5 * C] as const;
  const tri = (pts: string, f: string, k: string) => <polygon key={k} points={pts} fill={f} stroke="#14246a" strokeOpacity={0.35} />;
  const cx0 = o + 6 * C, cx1 = o + 9 * C, cy0 = t + 6 * C, cy1 = t + 9 * C;
  return (
    <>
      <Bg u={u} a="#ff7aa8" b="#8a2bd6" glow={0.5} cy={38} />
      <L0>
        <Rays cx={320} cy={150} n={20} len={560} color="#fff" opacity={0.13} />
        {[[66, 70], [580, 60], [92, 290], [560, 300]].map(([x, y], i) => <Sparkle key={i} x={x!} y={y!} s={i % 2 ? 9 : 14} o={0.9} />)}
      </L0>
      <L1>
        <rect x={o - 12} y={t - 12} width={15 * C + 24} height={15 * C + 24} rx={20} fill="#000" opacity={0.2} transform="translate(5 9)" />
        <rect x={o - 12} y={t - 12} width={15 * C + 24} height={15 * C + 24} rx={20} fill="#ffc61a" stroke="#d98200" strokeWidth={3} />
        <rect x={o - 5} y={t - 5} width={15 * C + 10} height={15 * C + 10} rx={12} fill="#fffaf0" />
        {base(0, 0, P.r)}{base(9, 0, P.b)}{base(9, 9, P.y)}{base(0, 9, P.g)}
        {cells}
        {tri(`${cx0},${cy0} ${cx0},${cy1} ${centre[0]},${centre[1]}`, P.r[0], "tr")}
        {tri(`${cx0},${cy0} ${cx1},${cy0} ${centre[0]},${centre[1]}`, P.b[0], "tb")}
        {tri(`${cx1},${cy0} ${cx1},${cy1} ${centre[0]},${centre[1]}`, P.y[0], "ty")}
        {tri(`${cx0},${cy1} ${cx1},${cy1} ${centre[0]},${centre[1]}`, P.g[0], "tg")}
      </L1>
      <L2>
        <Pawn x={o + 2.5 * C} y={t + 3.2 * C} s={1.15} c={P.r[0]} d={P.r[1]} />
        <Pawn x={o + 4 * C} y={t + 4.4 * C} s={1.15} c={P.r[0]} d={P.r[1]} />
        <Pawn x={o + 11.5 * C} y={t + 2.8 * C} s={1.15} c={P.b[0]} d={P.b[1]} />
        <Pawn x={o + 11.5 * C} y={t + 11.8 * C} s={1.15} c={P.y[0]} d={P.y[1]} />
        <Pawn x={o + 3 * C} y={t + 11 * C} s={1.15} c={P.g[0]} d={P.g[1]} />
        <Pawn x={o + 7.5 * C} y={t + 4.3 * C} s={1.3} c={P.b[0]} d={P.b[1]} />
        <Die x={512} y={120} s={74} n={6} rot={14} />
        <Die x={118} y={246} s={56} n={3} rot={-16} />
        <Star5 x={o + 7.5 * C} y={t + 7.5 * C - 2} r={11} />
      </L2>
    </>
  );
}

/* ─────────────── Yatzy: five dice on the felt and a score sheet ─────────────── */
export function Yatzy({ u }: SceneProps) {
  const rows = ["Ettere", "Toere", "Treere", "Par", "Chance", "Yatzy"];
  return (
    <>
      <Bg u={u} a="#ffcf5c" b="#e0661a" glow={0.55} cy={30} />
      <L0>
        <Rays cx={320} cy={70} n={22} len={560} color="#fff2b0" opacity={0.18} />
        {[[62, 54], [588, 50], [40, 190], [604, 214]].map(([x, y], i) => <Sparkle key={i} x={x!} y={y!} s={i % 2 ? 9 : 13} />)}
      </L0>
      <L1>
        <ellipse cx={320} cy={300} rx={290} ry={64} fill="#1f8d5a" />
        <ellipse cx={320} cy={300} rx={290} ry={64} fill="none" stroke="#8a5a1e" strokeWidth={8} />
        <ellipse cx={320} cy={300} rx={276} ry={54} fill="#2fb173" opacity={0.7} />
        <g transform="translate(46 56) rotate(-5)">
          <rect x={5} y={9} width={196} height={212} rx={14} fill="#000" opacity={0.2} />
          <rect width={196} height={212} rx={14} fill="#fffdf4" stroke="#d6c28a" strokeWidth={3} />
          <rect width={196} height={30} rx={14} fill="#14246a" />
          <rect y={16} width={196} height={14} fill="#14246a" />
          <text x={98} y={22} textAnchor="middle" fontSize={17} fontWeight={900} fill="#ffd23f" style={{ fontFamily: "var(--display)" }}>POINT</text>
          {rows.map((r, i) => (
            <g key={r} transform={`translate(0 ${42 + i * 28})`}>
              <line x1={10} x2={186} y1={22} y2={22} stroke="#c9d4f2" strokeWidth={2} />
              <text x={12} y={16} fontSize={14} fontWeight={800} fill="#14246a" style={{ fontFamily: "var(--display)" }}>{r}</text>
              {[3, 6, 12, 8, 24, 50][i] !== undefined && i !== 3 && i !== 5 && <text x={178} y={16} textAnchor="end" fontSize={15} fontWeight={900} fill="#0f8a3e" style={{ fontFamily: "var(--display)" }}>{[3, 6, 12, 8, 24, 50][i]}</text>}
              {i === 5 && <g><circle cx={160} cy={11} r={12} fill="#ffc61a" stroke="#d98200" strokeWidth={2} /><text x={160} y={16} textAnchor="middle" fontSize={13} fontWeight={900} fill="#7a3d00" style={{ fontFamily: "var(--display)" }}>50</text></g>}
            </g>
          ))}
        </g>
      </L1>
      <L2>
        <Die x={316} y={222} s={74} n={5} rot={-12} />
        <Die x={414} y={196} s={74} n={5} rot={10} />
        <Die x={512} y={236} s={74} n={5} rot={-6} />
        <Die x={376} y={286} s={66} n={5} rot={18} />
        <Die x={468} y={306} s={66} n={2} rot={-22} body="#fff6cf" />
        <Star5 x={560} y={88} r={22} rot={14} />
        <Coin x={532} y={140} r={20} rot={-10} />
        <Pill x={322} y={52} w={160} h={40} fill="#14246a" text="YATZY!" tc="#ffd23f" fs={23} />
      </L2>
    </>
  );
}

/* ─────────────── Sudoku: the grid, one fogged box and the number keys ─────────────── */
export function Sudoku({ u }: SceneProps) {
  const N = 6, C = 38, o = 320 - (N * C) / 2, t = 28;
  const digits: Record<string, number> = { "0,0": 3, "1,0": 5, "3,0": 2, "5,0": 1, "2,1": 1, "4,1": 6, "0,2": 6, "2,2": 4, "5,2": 2, "1,3": 2, "3,3": 5, "4,3": 3, "0,4": 4, "2,4": 3, "5,4": 5, "1,5": 6, "3,5": 4, "5,5": 3 };
  const mine = new Set(["2,1", "4,3", "1,5"]);
  const cells: ReactNode[] = [];
  for (let i = 0; i < N; i++) for (let j = 0; j < N; j++) {
    const k = `${i},${j}`, d = digits[k];
    cells.push(
      <g key={k}>
        <rect x={o + i * C} y={t + j * C} width={C} height={C} fill={d && !mine.has(k) ? "#eef2ff" : "#fff"} stroke="#b9c6ea" strokeWidth={1} />
        {d && <text x={o + i * C + C / 2} y={t + j * C + C * 0.7} textAnchor="middle" fontSize={25} fontWeight={900} fill={mine.has(k) ? "#2f7bff" : "#14246a"} style={{ fontFamily: "var(--display)" }}>{d}</text>}
      </g>,
    );
  }
  return (
    <>
      <Bg u={u} a="#58b7ff" b="#1c3fb8" glow={0.55} cy={34} />
      <L0>
        <Rays cx={320} cy={150} n={20} len={560} color="#fff" opacity={0.13} />
        {[[60, 66], [586, 52], [84, 292], [574, 290]].map(([x, y], i) => <Sparkle key={i} x={x!} y={y!} s={i % 2 ? 9 : 13} />)}
        {[[78, 110, 3], [548, 176, 7], [92, 200, 5], [556, 90, 2]].map(([x, y, d], i) => <text key={i} x={x} y={y} fontSize={40} fontWeight={900} fill="#fff" opacity={0.2} style={{ fontFamily: "var(--display)" }}>{d}</text>)}
      </L0>
      <L1>
        <rect x={o - 12} y={t - 12} width={N * C + 24} height={N * C + 24} rx={16} fill="#000" opacity={0.2} transform="translate(5 9)" />
        <rect x={o - 12} y={t - 12} width={N * C + 24} height={N * C + 24} rx={16} fill="#ffc61a" stroke="#d98200" strokeWidth={3} />
        <rect x={o} y={t} width={N * C} height={N * C} fill="#fff" />
        {cells}
        {[0, 3, 6].map((i) => <line key={`v${i}`} x1={o + i * C} x2={o + i * C} y1={t} y2={t + N * C} stroke="#14246a" strokeWidth={i === 0 || i === 6 ? 4 : 3} />)}
        {[0, 2, 4, 6].map((j) => <line key={`h${j}`} y1={t + j * C} y2={t + j * C} x1={o} x2={o + N * C} stroke="#14246a" strokeWidth={j === 0 || j === 6 ? 4 : 3} />)}
        <g>
          <rect x={o + 3 * C} y={t + 4 * C} width={3 * C} height={2 * C} fill="#4a5cc0" opacity={0.7} />
          <rect x={o + 3 * C + 2} y={t + 4 * C + 2} width={3 * C - 4} height={2 * C - 4} fill="none" stroke="#fff" strokeOpacity={0.7} strokeWidth={3} />
          <circle cx={o + 4.5 * C} cy={t + 5 * C} r={24} fill="#14246a" />
          <text x={o + 4.5 * C} y={t + 5 * C + 14} textAnchor="middle" fontSize={44} fontWeight={900} fill="#ffd23f" style={{ fontFamily: "var(--display)" }}>?</text>
        </g>
      </L1>
      <L2>
        {[1, 2, 3, 4, 5, 6].map((d, i) => (
          <g key={d} transform={`translate(${o + 2 + i * 38} ${t + N * C + 28}) rotate(${(i % 2 ? 1 : -1) * 3})`}>
            <rect x={1} y={4} width={34} height={42} rx={9} fill="#14246a" opacity={0.3} />
            <rect width={34} height={42} rx={9} fill={d === 4 ? "#ffd23f" : "#fff"} stroke={d === 4 ? "#d98200" : "#c3cff0"} strokeWidth={2} />
            <text x={17} y={31} textAnchor="middle" fontSize={26} fontWeight={900} fill="#14246a" style={{ fontFamily: "var(--display)" }}>{d}</text>
          </g>
        ))}
        <Star5 x={560} y={120} r={24} rot={12} />
        <Star5 x={86} y={156} r={16} rot={-12} />
      </L2>
    </>
  );
}

/* ─────────────── Videnspar: a grid of cards, two of them turned up ─────────────── */
function Back({ x, y, w, h, r = 0 }: { x: number; y: number; w: number; h: number; r?: number }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${r} ${w / 2} ${h / 2})`}>
      <rect x={2} y={6} width={w} height={h} rx={12} fill="#000" opacity={0.22} />
      <rect width={w} height={h} rx={12} fill="#3d8bff" stroke="#fff" strokeWidth={3} />
      <rect x={7} y={7} width={w - 14} height={h - 14} rx={8} fill="none" stroke="#fff" strokeOpacity={0.5} strokeWidth={2} strokeDasharray="5 5" />
      <path d={`M${w / 2} ${h / 2 - 17} l4.5 11 12 1 -9 8 3 12 -10.5 -6.5 -10.5 6.5 3 -12 -9 -8 12 -1Z`} fill="#ffd23f" opacity={0.95} />
    </g>
  );
}
function Face({ x, y, w, h, r = 0, ok = false, children }: { x: number; y: number; w: number; h: number; r?: number; ok?: boolean; children: ReactNode }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${r} ${w / 2} ${h / 2})`}>
      <rect x={2} y={6} width={w} height={h} rx={12} fill="#000" opacity={0.22} />
      <rect width={w} height={h} rx={12} fill={ok ? "#d8f8e3" : "#fff"} stroke={ok ? "#22a05a" : "#d4defa"} strokeWidth={4} />
      <g transform={`translate(${w / 2} ${h / 2})`}>{children}</g>
    </g>
  );
}
export function Videnspar({ u }: SceneProps) {
  const W = 88, H = 112, G = 16, cols = 4, rows = 2;
  const ox = 320 - (cols * W + (cols - 1) * G) / 2 - 90, oy = 54;
  const slots: ReactNode[] = [];
  const faces: Record<string, ReactNode> = {
    "1,0": <g key="f10"><circle r={26} fill="#ffc61a" stroke="#d98200" strokeWidth={3} /><circle cx={-9} cy={-5} r={3.5} fill="#14246a" /><circle cx={9} cy={-5} r={3.5} fill="#14246a" /><path d="M-12 8 Q0 20 12 8" fill="none" stroke="#14246a" strokeWidth={4} strokeLinecap="round" /></g>,
    "2,1": <g key="f21"><circle r={26} fill="#ffc61a" stroke="#d98200" strokeWidth={3} /><circle cx={-9} cy={-5} r={3.5} fill="#14246a" /><circle cx={9} cy={-5} r={3.5} fill="#14246a" /><path d="M-12 8 Q0 20 12 8" fill="none" stroke="#14246a" strokeWidth={4} strokeLinecap="round" /></g>,
    "3,0": <Star5 key="f30" x={0} y={2} r={32} />,
    "0,1": <Star5 key="f01" x={0} y={2} r={32} />,
  };
  for (let j = 0; j < rows; j++) for (let i = 0; i < cols; i++) {
    const x = ox + i * (W + G), y = oy + j * (H + G), k = `${i},${j}`;
    slots.push(faces[k]
      ? <Face key={k} x={x} y={y} w={W} h={H} r={k === "1,0" ? -5 : k === "2,1" ? 4 : 0} ok={k === "3,0" || k === "0,1"}>{faces[k]}</Face>
      : <Back key={k} x={x} y={y} w={W} h={H} r={(i * 3 + j * 5) % 7 - 3} />);
  }
  return (
    <>
      <Bg u={u} a="#69e0a0" b="#0f8a5c" glow={0.55} cy={34} />
      <L0>
        <Rays cx={320} cy={150} n={20} len={560} color="#e8ffd0" opacity={0.17} />
        {[[56, 66], [588, 58], [66, 310], [590, 306]].map(([x, y], i) => <Sparkle key={i} x={x!} y={y!} s={i % 2 ? 9 : 13} />)}
      </L0>
      <L1>
        <rect x={ox - 22} y={oy - 20} width={cols * W + (cols - 1) * G + 44} height={rows * H + G + 40} rx={26} fill="#000" opacity={0.2} transform="translate(5 9)" />
        <rect x={ox - 22} y={oy - 20} width={cols * W + (cols - 1) * G + 44} height={rows * H + G + 40} rx={26} fill="#2fb173" stroke="#8a5a1e" strokeWidth={7} />
        {slots}
      </L1>
      <L2>
        <Face x={500} y={142} w={96} h={124} r={9} ok><Star5 x={0} y={2} r={36} /></Face>
        <Face x={452} y={216} w={96} h={124} r={-8}><g><Star5 x={0} y={2} r={36} /></g></Face>
        <g transform="translate(576 120)"><circle r={22} fill="#22a05a" stroke="#fff" strokeWidth={4} /><path d="M-10 0 L-3 8 L11 -8" fill="none" stroke="#fff" strokeWidth={5} strokeLinecap="round" strokeLinejoin="round" /></g>
        <Pill x={470} y={52} w={150} h={40} fill="#14246a" text="PAR! +100" tc="#ffd23f" fs={20} />
        <Coin x={598} y={316} r={18} rot={-8} />
      </L2>
    </>
  );
}

/* v19 – Fri Verden: an island, a road, a little red car and a quiz stop */
export function Verden({ u }: SceneProps) {
  return (
    <>
      <Bg u={u} a="#7fe3ff" b="#1aa5c9" glow={0.6} cy={28} />
      <L0>
        <Rays cx={460} cy={80} n={18} len={600} color="#fff7c2" opacity={0.15} />
        <circle cx={500} cy={84} r={34} fill="#fff3a8" />
        <circle cx={500} cy={84} r={22} fill="#ffe066" />
        <g fill="#fff" opacity={0.9}><ellipse cx={120} cy={76} rx={52} ry={16} /><ellipse cx={156} cy={64} rx={34} ry={14} /><ellipse cx={300} cy={52} rx={44} ry={12} /></g>
      </L0>
      <L1>
        <path d="M0 232 Q120 214 240 226 T480 220 T640 232 V360 H0Z" fill="#1f8fb8" opacity={0.55} />
        <path d="M-10 262 Q90 188 210 214 Q300 150 400 196 Q520 170 650 250 V360 H-10Z" fill="#53c36b" />
        <path d="M120 250 Q230 200 310 206 Q400 212 470 246" fill="none" stroke="#f3dca0" strokeWidth={10} opacity={0.7} />
        <path d="M-10 300 Q160 238 320 262 T660 292" fill="none" stroke="#454c5e" strokeWidth={44} strokeLinecap="round" />
        <path d="M-10 300 Q160 238 320 262 T660 292" fill="none" stroke="#fff" strokeWidth={4} strokeDasharray="22 20" />
        {[[70, 218], [112, 232], [532, 214], [574, 228]].map(([x, y], i) => <g key={i} transform={`translate(${x} ${y})`}><rect x={-3} y={0} width={6} height={18} fill="#8a5a1e" /><path d="M0 -34 L18 6 H-18Z" fill="#1e8f4a" /><path d="M0 -22 L15 14 H-15Z" fill="#26a85a" /></g>)}
        <g transform="translate(398 176)"><rect x={-26} y={-30} width={52} height={36} rx={4} fill="#ffd23f" stroke="#d98200" strokeWidth={3} /><path d="M-30 -30 L0 -52 L30 -30Z" fill="#ef4444" /><rect x={-7} y={-12} width={14} height={18} fill="#14246a" /></g>
      </L1>
      <L2>
        <g transform="translate(250 290) rotate(-4)">
          <ellipse cx={0} cy={26} rx={74} ry={9} fill="#000" opacity={0.25} />
          <path d="M-70 14 Q-72 -10 -44 -14 L-26 -38 Q-8 -50 26 -40 L48 -14 Q76 -10 72 14 Z" fill="#ef4444" stroke="#9f1239" strokeWidth={4} strokeLinejoin="round" />
          <path d="M-22 -30 Q-6 -40 20 -32 L34 -14 H-34Z" fill="#bde9ff" stroke="#9f1239" strokeWidth={3} />
          <circle cx={-6} cy={-22} r={11} fill="#ffc9a3" stroke="#14246a" strokeWidth={3} />
          <circle cx={-44} cy={20} r={17} fill="#1f2937" stroke="#fff" strokeWidth={3} /><circle cx={44} cy={20} r={17} fill="#1f2937" stroke="#fff" strokeWidth={3} />
          <circle cx={-44} cy={20} r={6} fill="#cbd5e1" /><circle cx={44} cy={20} r={6} fill="#cbd5e1" />
        </g>
        <g transform="translate(540 214)"><rect x={-4} y={-70} width={8} height={78} fill="#e2e8f0" stroke="#94a3b8" strokeWidth={2} /><rect x={-4} y={-70} width={42} height={30} fill="#22c55e" stroke="#15803d" strokeWidth={3} /><text x={34} y={-50} fontSize={20} fontWeight={900} fill="#fff" textAnchor="end">?</text></g>
        <Pill x={40} y={26} w={188} h={40} fill="#14246a" text="3D-ØEN!" tc="#ffd23f" fs={22} />
        <Pill x={470} y={300} w={152} h={36} fill="#fff" text="3 spørgsmål/omgang" tc="#14246a" fs={13} />
        <Coin x={600} y={42} r={18} rot={-8} />
      </L2>
    </>
  );
}
